import { WA_WINDOW } from '@ng-web-apis/common';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class NetworkService {
  private readonly window = inject(WA_WINDOW);
  private readonly online$ = new BehaviorSubject<boolean>(
    this.window.navigator.onLine,
  );
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private locale = this.outkeepTranslateService.getLocaleStr();
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.window.addEventListener('online', () => this.updateOnlineStatus(true));
    this.window.addEventListener('offline', () =>
      this.updateOnlineStatus(false),
    );
    this.outkeepTranslateService
      .onLangChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.locale = this.outkeepTranslateService.getLocaleStr();
      });
  }
  get isOnline$(): Observable<boolean> {
    return this.online$.asObservable();
  }

  private updateOnlineStatus(isOnline: boolean): void {
    this.online$.next(isOnline);
    if (!isOnline) {
      this.saveLastOnlineStatus();
    }
  }

  private saveLastOnlineStatus(): void {
    const date = new Date().toISOString();

    localStorage.setItem('lastOnlineStatus', JSON.stringify({ date }));
  }

  public getLastOnlineStatus(): string | null {
    const lastOnlineStatus = localStorage.getItem('lastOnlineStatus');

    if (!lastOnlineStatus) {
      return null;
    }

    const { date } = JSON.parse(lastOnlineStatus);

    return new Date(date).toLocaleString(this.locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  public clearLastOnlineStatus(): void {
    localStorage.removeItem('lastOnlineStatus');
  }
}
